import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GatewaySchema, type Gateway, type PaymentIntentView } from '@capsico/contracts';
import { errorMessage } from '../api/client';
import { confirmReturn, fetchIntent } from '../api/payments';
import { queryParams } from '../utils/gateway';

const POLL_MS = 3000;
/** After this long still "pending", offer to leave and watch the order page instead. */
const PATIENCE_MS = 60_000;

/**
 * /pay/return (W11): a gateway sent the customer back with ?intent=…&gw=… plus its own fields.
 * Everything is handed to the server, which checks with the gateway; while the gateway is still
 * settling the attempt is re-read every 3 s. The page only shows what the server decided.
 */
export function usePayReturn(search: string) {
  const params = queryParams(search);
  const parsed = GatewaySchema.safeParse(params.gw);
  const gateway: Gateway | null = parsed.success ? parsed.data : null;
  const intentId = params.intent ?? null;
  const valid = gateway !== null && intentId !== null;

  const returned = useQuery({
    queryKey: ['pay-return', search],
    queryFn: () => confirmReturn(gateway as Gateway, params),
    enabled: valid,
    retry: 2,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const stillPending = returned.data?.status === 'pending';
  const polled = useQuery({
    queryKey: ['payment-intent', intentId ?? 'none'],
    queryFn: () => fetchIntent(intentId ?? ''),
    enabled: valid && stillPending,
    refetchInterval: (current) =>
      !current.state.data || current.state.data.status === 'pending' ? POLL_MS : false,
    staleTime: 0,
  });

  const [patient, setPatient] = useState(true);
  const started = useRef(Date.now());
  useEffect(() => {
    if (!stillPending) return;
    const wait = Math.max(0, PATIENCE_MS - (Date.now() - started.current));
    const timer = window.setTimeout(() => setPatient(false), wait);
    return () => window.clearTimeout(timer);
  }, [stillPending]);

  const intent: PaymentIntentView | null =
    (stillPending && polled.data ? polled.data : returned.data) ?? null;
  const status = !valid
    ? 'invalid'
    : returned.isError
      ? 'error'
      : !intent || intent.status === 'pending'
        ? 'checking'
        : intent.status === 'paid'
          ? 'paid'
          : 'failed';

  // Once the server has decided, everything that shows this order or the wallet is stale.
  const client = useQueryClient();
  const decided = status === 'paid' || status === 'failed' ? intent?.orderId : undefined;
  useEffect(() => {
    if (!decided) return;
    void client.invalidateQueries({ queryKey: ['my-order', decided] });
    void client.invalidateQueries({ queryKey: ['my-orders'] });
    void client.invalidateQueries({ queryKey: ['wallet'] });
  }, [client, decided]);

  return {
    status: status as 'invalid' | 'error' | 'checking' | 'paid' | 'failed',
    gateway,
    intent,
    orderId: intent?.orderId ?? null,
    error: returned.error ? errorMessage(returned.error) : null,
    /** Still checking after a minute: let the customer go to the order page. */
    slow: status === 'checking' && !patient,
    retry: () => void returned.refetch(),
  };
}
